
interface Product {
  id: number;
  title: string;
  category: string;
  brand: string;
  model: string;
  certification: string;
  status: string;
  image: string;
  description: string;
}

interface ProductDetailHeroProps {
  product: Product;
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a href="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-red-600">
                <i className="ri-home-line w-4 h-4 mr-2"></i>
                Home
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <a href="/products" className="ml-1 text-sm font-medium text-gray-700 hover:text-red-600 md:ml-2">Products</a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">{product.title}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square bg-white rounded-2xl shadow-xl overflow-hidden">
              <img
                src={product.image}
                alt={product.title}
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <i className="ri-check-line w-3 h-3 mr-1"></i>
                {product.status}
              </span>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-red-600 mb-2">{product.category}</p>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.title}</h1>
              <p className="text-lg text-gray-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Product Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border">
                <p className="text-sm text-gray-500">Brand</p>
                <p className="font-semibold text-gray-900">{product.brand}</p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <p className="text-sm text-gray-500">Model</p>
                <p className="font-semibold text-gray-900">{product.model}</p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <p className="text-sm text-gray-500">Certification</p>
                <p className="font-semibold text-gray-900">{product.certification}</p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <p className="text-sm text-gray-500">Category</p>
                <p className="font-semibold text-gray-900">{product.category}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line mr-2"></i>
                Request Quote
              </button>
              <button className="flex-1 bg-white text-red-600 border-2 border-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-download-line mr-2"></i>
                Download Datasheet
              </button>
            </div>

            {/* Share Options */}
            <div className="flex items-center space-x-4 pt-4 border-t">
              <span className="text-sm text-gray-500">Share:</span>
              <button className="text-gray-400 hover:text-blue-600 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </button>
              <button className="text-gray-400 hover:text-blue-400 transition-colors cursor-pointer">
                <i className="ri-twitter-fill text-xl"></i>
              </button>
              <button className="text-gray-400 hover:text-blue-700 transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-xl"></i>
              </button>
              <button className="text-gray-400 hover:text-green-600 transition-colors cursor-pointer">
                <i className="ri-whatsapp-fill text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
